<template>
  <div class="custom-fields-container">
    <base-loader v-if="dataIsLoading" />

    <div v-else>
      <base-nav
        title="Пользовательские поля"
        :show-back="true"
        back-fallback="Home"
        :back-fallback-pattern="/\/requisite_types/"
      >
        <base-primary-button
          class="nav-btn"
          @click="toggleCreateForm"
        >
          {{ showCreateForm ? "Отмена" : "Создать" }}
        </base-primary-button>
      </base-nav>

      <!-- Форма создания -->
      <transition name="fade-slide">
        <div v-if="showCreateForm" class="card cf-form-wrap">
          <h4 class="form-title">Создание пользовательского поля</h4>
          <RequisiteForm
            :is-loading="customFieldsLoading"
            @save="handleCreate"
            @cancel="showCreateForm = false"
          />
        </div>
      </transition>

      <div v-if="localFields.length === 0" class="no-data">
        Пользовательские поля не созданы
      </div>

      <ul v-else class="cf-list">
        <BaseSettingsListItemCard
          v-for="field in localFields"
          :key="field.id"
          :enabled="field.enabled"
          :is-editing="editingFieldId === field.id"
          :is-loading="customFieldsLoading"
          edit-title="Редактирование пользовательского поля"
          :show-toggle="true"
          :show-delete="false"
          @edit="startEdit(field)"
          @toggle="toggleField(field)"
        >
          <template #edit>
            <RequisiteForm
              :initial-data="field"
              :is-loading="customFieldsLoading"
              @save="(payload) => handleUpdate(field.id, payload)"
              @cancel="editingFieldId = null"
            />
          </template>

          <div class="cf-top">
            <span class="cf-name">{{ field.name }}</span>
            <span class="cf-code">{{ field.code }}</span>
          </div>

          <p v-if="field.description" class="cf-description">
            {{ field.description }}
          </p>

          <div class="cf-badges">
            <span
              class="cf-badge"
              :class="
                field.allow_attachments
                  ? 'cf-badge--green'
                  : 'cf-badge--red'
              "
            >
              {{
                field.allow_attachments
                  ? "Вложения разрешены"
                  : "Вложения запрещены"
              }}
            </span>
          </div>

          <!-- Модели -->
          <div v-if="field.allowed_models.length" class="cf-meta-row">
            <span class="cf-meta-label">Модели:</span>
            <div class="cf-badges">
              <span
                v-for="model in field.allowed_models"
                :key="model"
                class="cf-badge cf-badge--grey"
              >
                {{ formatModel(model) }}
              </span>
            </div>
          </div>

          <!-- Поля схемы -->
          <div
            v-if="field.schema.fields.length"
            class="cf-fields-block"
          >
            <span class="cf-meta-label">Поля:</span>
            <ul class="cf-fields-list">
              <li
                v-for="(schemaField, idx) in field.schema.fields"
                :key="idx"
                class="cf-fields-item"
              >
                <span class="cf-fields-name">
                  {{ schemaField.label }}
                </span>
                <span class="cf-fields-type">
                  {{
                    fieldTypeNames[schemaField.type] ||
                    schemaField.type
                  }}
                </span>
              </li>
            </ul>
          </div>
        </BaseSettingsListItemCard>
      </ul>

      <!-- Пагинация -->
      <pagination-component
        v-if="customFieldsPagination.pages > 1"
        :page="customFieldsPagination.page"
        :pages="customFieldsPagination.pages"
        :series="customFieldsPagination.series"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useRequisiteStore } from "@/stores/requisiteStore";
import { useNotificationStore } from "@/stores/notificationStore";

import { transliterate } from "@/utils/translitUtils";
import { generateUniqueCode } from "@/utils/codeGenerator";

import RequisiteForm from "@/components/Requisite/RequisiteForm.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";

export default {
  name: "RequisiteTypesPage",
  components: {
    RequisiteForm,
    PaginationComponent,
  },

  data() {
    return {
      dataIsLoading: true,
      showCreateForm: false,
      editingFieldId: null,
      localFields: [],
    };
  },

  computed: {
    ...mapState(useRequisiteStore, [
      "customFields",
      "customFieldsLoading",
      "customFieldsPagination",
      "fieldTypeNames",
      "availableModels",
    ]),

    notificationStore() {
      return useNotificationStore();
    },
  },

  watch: {
    "$route.query.page": {
      immediate: false,
      handler(newPage) {
        this.loadFields(newPage);
      },
    },
  },

  methods: {
    ...mapActions(useRequisiteStore, [
      "getCustomFieldsRequest",
      "createCustomField",
      "updateCustomField",
      "toggleCustomField",
      "updateLocalCustomField",
    ]),

    async loadFields(page) {
      const pageNumber =
        parseInt(page) || parseInt(this.$route.query.page) || 1;
      try {
        this.dataIsLoading = true;
        await this.getCustomFieldsRequest({ page: pageNumber });
        this.localFields = [...this.customFields];

        if (this.$route.query.page != String(pageNumber)) {
          this.$router
            .replace({
              query: { ...this.$route.query, page: pageNumber },
            })
            .catch(() => {});
        }
      } finally {
        this.dataIsLoading = false;
      }
    },

    formatModel(model) {
      const found = this.availableModels.find(
        (am) => am.value === model
      );
      return found ? found.label : model;
    },

    formatModels(models) {
      return models.map((m) => this.formatModel(m)).join(", ");
    },

    // Подготовка payload: генерация code для типа и полей схемы
    preparePayload(payload) {
      const prepared = { ...payload };

      if (!prepared.code) {
        prepared.code = transliterate(prepared.name) || "";
      }

      const fields = prepared.schema?.fields || [];
      const usedCodes = new Set(
        fields
          .map((f) => (f.code || "").trim())
          .filter((c) => c.length > 0)
      );

      fields.forEach((field) => {
        if (!field.code || field.code.trim() === "") {
          const base = (field.label || "field").trim();
          const unique = generateUniqueCode(
            base,
            Array.from(usedCodes)
          );
          field.code = unique;
          usedCodes.add(unique);
        }

        if (field.type === "select" && field.options) {
          const usedValues = new Set();
          field.options.forEach((option) => {
            if (!option.value || option.value.trim() === "") {
              const base = (option.label || "option").trim();
              const unique = generateUniqueCode(
                base,
                Array.from(usedValues)
              );
              option.value = unique;
              usedValues.add(unique);
            } else {
              usedValues.add(option.value.trim());
            }
          });
        }
      });

      return prepared;
    },

    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm;
      if (this.showCreateForm) {
        this.editingFieldId = null;
      }
    },

    startEdit(field) {
      this.editingFieldId = field.id;
      this.showCreateForm = false;
    },

    async handleCreate(payload) {
      try {
        const prepared = this.preparePayload(payload);
        await this.createCustomField(prepared);
        await this.loadFields();
        this.showCreateForm = false;
        this.notificationStore.showSuccess(
          "Пользовательское поле создано"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async handleUpdate(id, payload) {
      try {
        const prepared = this.preparePayload(payload);
        await this.updateCustomField(id, prepared);
        await this.loadFields();
        this.editingFieldId = null;
        this.notificationStore.showSuccess(
          "Пользовательское поле обновлено"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async toggleField(field) {
      try {
        await this.toggleCustomField(field.id);
        this.updateLocalCustomField({
          ...field,
          enabled: !field.enabled,
        });
        this.localFields = [...this.customFields];
        this.notificationStore.showSuccess(
          field.enabled
            ? "Пользовательское поле включено"
            : "Пользовательское поле отключено"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },
  },

  async created() {
    await this.loadFields(this.$route.query.page);
  },
};
</script>

<style scoped>
.custom-fields-container {
  flex-grow: 1;
}

.cf-form-wrap {
  margin-bottom: var(--base-out-indent);
}

.form-title {
  margin-bottom: var(--base-in-indent);
}

.cf-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
  margin-bottom: 15px;
}

.cf-top {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.cf-name {
  font-weight: 600;
  font-size: 17px;
}

.cf-code {
  font-size: 13px;
  color: #666;
}

.cf-description {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.cf-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cf-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
}

.cf-badge--green {
  background-color: #dcfce7;
  color: #166534;
}

.cf-badge--red {
  background-color: #fee2e2;
  color: #991b1b;
}

.cf-badge--grey {
  background-color: #f0f0f0;
  color: #4b5563;
}

.cf-meta-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.cf-meta-label {
  font-size: 14px;
  color: #666;
  white-space: nowrap;
  padding-top: 3px;
}

.cf-fields-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cf-fields-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cf-fields-item {
  display: flex;
  align-items: baseline;
  gap: 5px;
  font-size: 14px;
}

.cf-fields-name {
  font-size: 16px;
  font-weight: 500;
  color: var(--color-dark);
}

.cf-fields-type {
  color: #999;
  flex-shrink: 0;
}
</style>
